import axios from 'axios';

/**
 * Shared axios instance for the Orbit portal.
 *
 * Base URL and the Authorization header are configured at app startup by
 * calling `configureApiClient()` once the app config and auth are available.
 * All API service modules should import this instance rather than creating
 * their own.
 */
export const apiClient = axios.create();

// ---------------------------------------------------------------------------
// API activity tracking
// ---------------------------------------------------------------------------

let activeApiRequestCount = 0;
const apiActivityListeners = new Set<(count: number) => void>();
let interceptorsRegistered = false;

function notifyApiActivityListeners() {
    for (const fn of apiActivityListeners) fn(activeApiRequestCount);
}

function incrementActiveApiRequests() {
    activeApiRequestCount += 1;
    notifyApiActivityListeners();
}

function decrementActiveApiRequests() {
    activeApiRequestCount = Math.max(0, activeApiRequestCount - 1);
    notifyApiActivityListeners();
}

export function subscribeToApiActivity(listener: (count: number) => void): () => void {
    apiActivityListeners.add(listener);
    listener(activeApiRequestCount);
    return () => {
        apiActivityListeners.delete(listener);
    };
}

export function getActiveApiRequestCount(): number {
    return activeApiRequestCount;
}

/**
 * Configures the shared axios instance with the API base URL and a request
 * interceptor that acquires and injects a bearer token before every request.
 *
 * Call once during app initialisation, inside the AuthProvider tree so that
 * `getAccessToken` is available.
 *
 * @param apiBaseUrl - The base URL for the backend API (from AppConfig).
 * @param getAccessToken - Async function that acquires the current bearer token
 *   via MSAL silent acquisition. Provided by `useAuth().getAccessToken`.
 */
export function configureApiClient(apiBaseUrl: string, getAccessToken: () => Promise<string>): void {
    apiClient.defaults.baseURL = apiBaseUrl;

    apiClient.interceptors.request.use(async (config) => {
        const token = await getAccessToken();
        config.headers.Authorization = `Bearer ${token}`;
        return config;
    });

    if (!interceptorsRegistered) {
        interceptorsRegistered = true;

        apiClient.interceptors.request.use(
            (config) => {
                incrementActiveApiRequests();
                return config;
            },
            // Request never left the client — increment did not happen, so no decrement.
            (error: unknown) => Promise.reject(error instanceof Error ? error : new Error(String(error))),
        );

        apiClient.interceptors.response.use(
            (response) => {
                decrementActiveApiRequests();
                return response;
            },
            (error: unknown) => {
                decrementActiveApiRequests();
                return Promise.reject(error instanceof Error ? error : new Error(String(error)));
            },
        );
    }
}
